import { useState, useMemo, useEffect } from 'react';
import { useAdminClientePerfil, getColorHex, isColorLight } from '../../../hooks/useAdminClientePerfil';
import type { LookCalendario } from '../../../hooks/useClienteCalendario';
import type { Prenda } from '../../../hooks/useClienteArmario';

interface CalendarioProps {
  looks: LookCalendario[];
  prendas: Prenda[];
  onAdd: (item: Omit<LookCalendario, 'id'>) => Promise<void>;
  onUpdate: (id: number, updates: Partial<LookCalendario>) => Promise<void>;
  onRemove: (id: number) => Promise<void>;
  loading: boolean;
  clienteId: number;
  error: string | null;
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

const OCASIONES = [
  { value: '', label: 'Todas' },
  { value: 'trabajo', label: 'Trabajo' },
  { value: 'social', label: 'Social' },
  { value: 'eventos', label: 'Eventos' },
  { value: 'deporte', label: 'Deporte' },
  { value: 'fin_de_semana', label: 'Fin de semana' },
];

const ESTACIONES = [
  { value: '', label: 'Todas' },
  { value: 'primavera', label: 'Primavera' },
  { value: 'verano', label: 'Verano' },
  { value: 'otoño', label: 'Otoño' },
  { value: 'invierno', label: 'Invierno' },
];

export default function Calendario({
  looks,
  prendas,
  onAdd,
  onUpdate,
  onRemove,
  loading,
  clienteId,
  error,
}: CalendarioProps) {
  const { perfil: adminPerfil } = useAdminClientePerfil(clienteId);
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [editing, setEditing] = useState<LookCalendario | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedPrendasIds, setSelectedPrendasIds] = useState<number[]>([]);
  const [prendaSearch, setPrendaSearch] = useState('');
  const [ocasionFilter, setOcasionFilter] = useState('');
  const [estacionFilter, setEstacionFilter] = useState('');
  const [previewLook, setPreviewLook] = useState<LookCalendario | null>(null);
  const [previewFromForm, setPreviewFromForm] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      setFormError(error);
    }
  }, [error]);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  const calendarLooks = useMemo(() => {
    const map = new Map<string, LookCalendario[]>();
    looks.forEach((look) => {
      const date = look.fecha;
      if (!map.has(date)) map.set(date, []);
      map.get(date)!.push(look);
    });
    return map;
  }, [looks]);

  useEffect(() => {
    if (editing) {
      setSelectedPrendasIds(editing.prendas_ids || []);
    } else if (showForm) {
      setSelectedPrendasIds([]);
    }
  }, [editing, showForm]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const handleDayClick = (day: number) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setShowForm(true);
    setEditing(null);
    setFormError(null);
    setPreviewFromForm(false);
  };

  const togglePrenda = (id: number) => {
    setSelectedPrendasIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredPrendas = useMemo(() => {
    if (!prendaSearch.trim()) return prendas;
    const q = prendaSearch.toLowerCase();
    return prendas.filter(
      (p) =>
        p.nombre.toLowerCase().includes(q) ||
        p.categoria.toLowerCase().includes(q) ||
        p.color.toLowerCase().includes(q)
    );
  }, [prendas, prendaSearch]);

  const handleSave = async (formData: FormData) => {
    setFormError(null);
    setSuccessMsg(null);
    const titulo = (formData.get('titulo') as string)?.trim();
    const notas = (formData.get('notas') as string)?.trim() || null;
    const ocasion = (formData.get('ocasion') as string)?.trim() || null;
    const estacion = (formData.get('estacion') as string)?.trim() || null;
    const fecha = (formData.get('fecha') as string) || selectedDate || today.toISOString().split('T')[0];
    const prendas_ids = selectedPrendasIds;

    if (!titulo || !fecha) {
      setFormError('Título y fecha son obligatorios.');
      return;
    }

    const payload: Omit<LookCalendario, 'id'> = {
      cliente_id: clienteId,
      titulo,
      fecha,
      notas,
      prendas_ids,
      ocasion,
      estacion,
      estado: 'planeado',
    };

    try {
      if (editing) {
        await onUpdate(editing.id, payload);
        setEditing(null);
        setSuccessMsg('Look actualizado correctamente.');
      } else {
        await onAdd(payload);
        setShowForm(false);
        setSelectedDate(null);
        setSelectedPrendasIds([]);
        setSuccessMsg('Look guardado en la base de datos.');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'No se pudo guardar. Revisa la conexión o los datos.';
      console.error('Error guardando look:', e);
      setFormError(msg);
    }
  };

  const dateStr = (day: number) =>
    `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const monthLooks = looks.filter((l) => {
    const d = new Date(l.fecha);
    return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
  });

  const filteredMonthLooks = useMemo(() => {
    return monthLooks.filter((l) => {
      const matchOcasion = !ocasionFilter || l.ocasion === ocasionFilter;
      const matchEstacion = !estacionFilter || l.estacion === estacionFilter;
      return matchOcasion && matchEstacion;
    });
  }, [monthLooks, ocasionFilter, estacionFilter]);

  const openPreview = (look: LookCalendario, fromForm = false) => {
    setPreviewLook(look);
    setPreviewFromForm(fromForm);
  };

  const closePreview = () => {
    setPreviewLook(null);
    setPreviewFromForm(false);
  };

  const handleConfirmDay = (lookId: number) => {
    onUpdate(lookId, { estado: 'usado' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-brand-black text-3xl font-light">Calendario de Looks</h1>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">
            Planifica qué vestir cada día
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditing(null);
            setFormError(null);
            setSelectedDate(today.toISOString().split('T')[0]);
          }}
          className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
        >
          + Nuevo look
        </button>
      </div>

      {/* Paleta de colores recomendada */}
      {adminPerfil?.paleta_colores && adminPerfil.paleta_colores.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 flex items-center justify-center text-brand-sage">
              <i className="ri-palette-line" />
            </div>
            <h3 className="font-sans text-brand-black text-xs font-medium uppercase tracking-wider">
              Colores recomendados para tus looks
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {adminPerfil.paleta_colores.map((color) => {
              const hex = getColorHex(color);
              const light = isColorLight(hex);
              return (
                <span
                  key={color}
                  className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-brand-gray-200 font-sans text-xs"
                  style={{ backgroundColor: hex + '20' }}
                >
                  <span
                    className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                    style={{ backgroundColor: hex }}
                  />
                  <span className={light ? 'text-brand-black' : 'text-brand-black'}>{color}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={ocasionFilter}
          onChange={(e) => setOcasionFilter(e.target.value)}
          className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
        >
          {OCASIONES.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={estacionFilter}
          onChange={(e) => setEstacionFilter(e.target.value)}
          className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
        >
          {ESTACIONES.map((e) => (
            <option key={e.value} value={e.value}>{e.label}</option>
          ))}
        </select>
        {(ocasionFilter || estacionFilter) && (
          <button
            onClick={() => {
              setOcasionFilter('');
              setEstacionFilter('');
            }}
            className="text-brand-gray-500 hover:text-brand-black font-sans text-sm flex items-center gap-1.5"
          >
            <i className="ri-close-circle-line" />
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Calendar header */}
      <div className="bg-white border border-brand-gray-200 rounded-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-brand-black text-xl">
            {monthNames[currentMonth]} {currentYear}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevMonth}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <button
              onClick={handleNextMonth}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => (
            <div key={d} className="text-center font-sans text-brand-gray-400 text-xs uppercase tracking-wider py-2">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay === 0 ? 6 : firstDay - 1 }, (_, i) => (
            <div key={`empty-${i}`} className="aspect-square rounded-lg" />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dStr = dateStr(day);
            const dayLooks = calendarLooks.get(dStr) || [];
            const isToday = dStr === today.toISOString().split('T')[0];
            return (
              <button
                key={day}
                onClick={() => handleDayClick(day)}
                className={`aspect-square rounded-lg p-1 flex flex-col items-start gap-0.5 transition-colors hover:bg-brand-gray-50 ${
                  isToday ? 'bg-brand-sage/20 border border-brand-sage' : 'border border-transparent'
                }`}
              >
                <span className={`font-sans text-sm ${isToday ? 'font-medium text-brand-black' : 'text-brand-gray-600'}`}>
                  {day}
                </span>
                {dayLooks.length > 0 && (
                  <div className="flex items-center -space-x-1.5 mt-0.5">
                    {dayLooks[0].prendas_ids.slice(0, 3).map((pid) => {
                      const prenda = prendas.find((p) => p.id === pid);
                      return prenda?.imagen_url ? (
                        <img
                          key={pid}
                          src={prenda.imagen_url}
                          alt={prenda.nombre}
                          className="w-5 h-5 rounded-full object-cover border border-white"
                          title={prenda.nombre}
                        />
                      ) : (
                        <div
                          key={pid}
                          className="w-5 h-5 rounded-full bg-brand-gray-100 flex items-center justify-center border border-white"
                          title={prenda?.nombre ?? `Prenda #${pid}`}
                        >
                          <i className="ri-t-shirt-line text-brand-gray-400 text-[8px]" />
                        </div>
                      );
                    })}
                    {dayLooks.length > 1 && (
                      <div className="w-5 h-5 rounded-full bg-brand-gray-100 flex items-center justify-center border border-white font-sans text-[8px] text-brand-gray-500">
                        +{dayLooks.length - 1}
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Formulario */}
      {(showForm || editing) && (
        <form
          key={editing ? `edit-${editing.id}` : `new-${selectedDate || 'today'}`}
          onSubmit={(e) => {
            e.preventDefault();
            handleSave(new FormData(e.currentTarget));
          }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Look' : 'Nuevo Look'}
            </h3>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditing(null);
                setFormError(null);
                setSelectedPrendasIds([]);
                setSelectedDate(null);
              }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 mt-0.5" />
              <p className="font-sans text-red-600 text-sm">{formError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Fecha *
              </label>
              <input
                name="fecha"
                type="date"
                defaultValue={editing?.fecha ?? selectedDate ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Título *
              </label>
              <input
                name="titulo"
                defaultValue={editing?.titulo ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Ocasión
              </label>
              <select
                name="ocasion"
                defaultValue={editing?.ocasion ?? ''}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white"
              >
                <option value="">Seleccionar...</option>
                {OCASIONES.slice(1).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Estación
              </label>
              <select
                name="estacion"
                defaultValue={editing?.estacion ?? ''}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white"
              >
                <option value="">Seleccionar...</option>
                {ESTACIONES.slice(1).map((e) => (
                  <option key={e.value} value={e.value}>{e.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Picker visual de prendas */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Seleccionar prendas del armario
              </label>
              <span className="font-sans text-brand-gray-400 text-xs">
                {selectedPrendasIds.length} seleccionada{selectedPrendasIds.length !== 1 ? 's' : ''}
              </span>
            </div>
            <input
              type="text"
              placeholder="Buscar prenda..."
              value={prendaSearch}
              onChange={(e) => setPrendaSearch(e.target.value)}
              className="mb-3 w-full sm:w-72 bg-white border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
            />
            {prendas.length === 0 ? (
              <div className="bg-brand-gray-50 rounded-xl p-4 text-center">
                <p className="font-sans text-brand-gray-400 text-sm">Tu armario está vacío. Añade prendas primero.</p>
              </div>
            ) : filteredPrendas.length === 0 ? (
              <div className="bg-brand-gray-50 rounded-xl p-4 text-center">
                <p className="font-sans text-brand-gray-400 text-sm">No se encontraron prendas con ese término.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {filteredPrendas.map((prenda) => {
                  const selected = selectedPrendasIds.includes(prenda.id);
                  return (
                    <button
                      key={prenda.id}
                      type="button"
                      onClick={() => togglePrenda(prenda.id)}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all text-left ${
                        selected
                          ? 'border-brand-black ring-1 ring-brand-black'
                          : 'border-transparent hover:border-brand-gray-300'
                      }`}
                    >
                      <div className="relative w-full aspect-[3/4] bg-brand-gray-50">
                        {prenda.imagen_url ? (
                          <img
                            src={prenda.imagen_url}
                            alt={prenda.nombre}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-brand-gray-300">
                            <i className="ri-t-shirt-line text-xl" />
                          </div>
                        )}
                        {selected && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-black text-white flex items-center justify-center">
                            <i className="ri-check-line text-xs" />
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="font-sans text-brand-black text-[11px] font-medium line-clamp-1">{prenda.nombre}</p>
                        <p className="font-sans text-brand-gray-400 text-[10px]">{prenda.categoria}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Selected previews compact */}
            {selectedPrendasIds.length > 0 && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="font-sans text-brand-gray-500 text-xs">Seleccionadas:</span>
                {selectedPrendasIds.map((pid) => {
                  const prenda = prendas.find((p) => p.id === pid);
                  if (!prenda) return null;
                  return (
                    <div key={pid} className="flex items-center gap-1.5 bg-brand-gray-50 rounded-lg pl-1 pr-2 py-1">
                      {prenda.imagen_url ? (
                        <img src={prenda.imagen_url} alt={prenda.nombre} className="w-6 h-6 rounded object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded bg-brand-gray-100 flex items-center justify-center">
                          <i className="ri-t-shirt-line text-brand-gray-400 text-[10px]" />
                        </div>
                      )}
                      <span className="font-sans text-brand-black text-[11px] font-medium">{prenda.nombre}</span>
                      <button
                        type="button"
                        onClick={() => togglePrenda(pid)}
                        className="w-4 h-4 flex items-center justify-center text-brand-gray-400 hover:text-red-500"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Vista previa del look button */}
          {selectedPrendasIds.length > 0 && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const tempLook: LookCalendario = {
                    id: editing?.id ?? 0,
                    cliente_id: editing?.cliente_id ?? 0,
                    fecha: editing?.fecha ?? selectedDate ?? today.toISOString().split('T')[0],
                    titulo: editing?.titulo ?? 'Vista previa',
                    notas: editing?.notas ?? null,
                    prendas_ids: selectedPrendasIds,
                    ocasion: editing?.ocasion ?? null,
                    estacion: editing?.estacion ?? null,
                    estado: editing?.estado ?? 'planeado',
                  };
                  openPreview(tempLook, true);
                }}
                className="inline-flex items-center gap-2 border border-brand-gray-200 text-brand-black font-sans text-sm px-5 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
              >
                <i className="ri-eye-line" />
                Vista previa del look
              </button>
            </div>
          )}

          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Notas
            </label>
            <textarea
              name="notas"
              defaultValue={editing?.notas ?? ''}
              rows={3}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap disabled:opacity-60"
            >
              {loading ? 'Guardando...' : 'Guardar Look'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditing(null);
                setFormError(null);
                setSelectedPrendasIds([]);
                setSelectedDate(null);
              }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Lista de looks del mes */}
      <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-sans text-brand-gray-500 text-[11px] tracking-wider uppercase font-medium">
            Looks de este mes
          </h3>
          <span className="font-sans text-brand-gray-400 text-xs">
            {filteredMonthLooks.length} de {monthLooks.length}
          </span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-brand-gray-500 text-sm py-10">
            <i className="ri-loader-4-line animate-spin" />
            <span>Cargando...</span>
          </div>
        ) : filteredMonthLooks.length === 0 ? (
          <p className="font-sans text-brand-gray-400 text-sm text-center py-10">
            No hay looks planificados este mes con esos filtros.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredMonthLooks
              .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
              .map((look) => (
                <div
                  key={look.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-brand-cream"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-brand-sage/40 flex items-center justify-center text-brand-black shrink-0">
                      <i className="ri-calendar-event-line" />
                    </div>
                    <div>
                      <p className="font-sans text-brand-black text-sm font-medium">
                        {look.titulo}
                      </p>
                      <p className="font-sans text-brand-gray-500 text-xs">
                        {new Date(look.fecha).toLocaleDateString('es-ES', {
                          weekday: 'long',
                          day: 'numeric',
                        })}
                        {look.ocasion && ` · ${look.ocasion}`}
                        {look.estacion && ` · ${look.estacion}`}
                      </p>
                      {/* Thumbnail stack of selected garments */}
                      {look.prendas_ids.length > 0 && (
                        <div className="flex items-center -space-x-2 mt-2">
                          {look.prendas_ids.slice(0, 4).map((pid) => {
                            const prenda = prendas.find((p) => p.id === pid);
                            return prenda?.imagen_url ? (
                              <img
                                key={pid}
                                src={prenda.imagen_url}
                                alt={prenda.nombre}
                                className="w-7 h-7 rounded-full object-cover border-2 border-white"
                                title={prenda.nombre}
                              />
                            ) : (
                              <div
                                key={pid}
                                className="w-7 h-7 rounded-full bg-brand-gray-100 flex items-center justify-center border-2 border-white"
                                title={prenda?.nombre ?? `Prenda #${pid}`}
                              >
                                <i className="ri-t-shirt-line text-brand-gray-400 text-[10px]" />
                              </div>
                            );
                          })}
                          {look.prendas_ids.length > 4 && (
                            <div className="w-7 h-7 rounded-full bg-brand-gray-100 flex items-center justify-center border-2 border-white font-sans text-[10px] text-brand-gray-500">
                              +{look.prendas_ids.length - 4}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {look.estado === 'planeado' && (
                      <button
                        onClick={() => handleConfirmDay(look.id)}
                        className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-black text-white font-sans text-xs hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
                        title="Marcar como usado"
                      >
                        <i className="ri-check-line" />
                        Confirmar día
                      </button>
                    )}
                    <button
                      onClick={() => openPreview(look)}
                      className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-brand-gray-600 hover:text-brand-black"
                      title="Vista previa"
                    >
                      <i className="ri-eye-line text-xs" />
                    </button>
                    <button
                      onClick={() => {
                        setEditing(look);
                        setShowForm(false);
                        setFormError(null);
                      }}
                      className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-brand-gray-600 hover:text-brand-black"
                      title="Editar"
                    >
                      <i className="ri-pencil-line text-xs" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('¿Eliminar este look?')) onRemove(look.id);
                      }}
                      className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-brand-gray-600 hover:text-red-600"
                      title="Eliminar"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Modal vista previa del look (collage) */}
      {previewLook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={closePreview} />
          <div className="relative bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-brand-black text-xl">
                {previewLook.titulo}
              </h3>
              <button
                onClick={closePreview}
                className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="space-y-1">
              <p className="font-sans text-brand-gray-500 text-sm">
                {new Date(previewLook.fecha).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {previewLook.ocasion && (
                  <span className="inline-block px-2 py-0.5 rounded-full bg-brand-gray-100 font-sans text-[11px] text-brand-gray-600">
                    {previewLook.ocasion}
                  </span>
                )}
                {previewLook.estacion && (
                  <span className="inline-block px-2 py-0.5 rounded-full bg-brand-gray-100 font-sans text-[11px] text-brand-gray-600">
                    {previewLook.estacion}
                  </span>
                )}
                <span className={`inline-block px-2 py-0.5 rounded-full font-sans text-[11px] ${
                  previewLook.estado === 'usado'
                    ? 'bg-brand-black text-white'
                    : 'bg-brand-sage/20 text-brand-black'
                }`}>
                  {previewLook.estado === 'usado' ? 'Usado' : 'Planificado'}
                </span>
              </div>
            </div>

            {/* Collage de prendas en grande */}
            <div className="grid grid-cols-2 gap-3">
              {previewLook.prendas_ids.map((pid, idx) => {
                const prenda = prendas.find((p) => p.id === pid);
                if (!prenda) return null;
                const isHero = idx === 0;
                return (
                  <div
                    key={pid}
                    className={`relative rounded-xl overflow-hidden bg-brand-gray-50 ${
                      isHero ? 'col-span-2 aspect-[16/9]' : 'aspect-[3/4]'
                    }`}
                  >
                    {prenda.imagen_url ? (
                      <img
                        src={prenda.imagen_url}
                        alt={prenda.nombre}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-brand-gray-300">
                        <i className="ri-t-shirt-line text-3xl" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <p className="font-sans text-white text-sm font-medium">{prenda.nombre}</p>
                      <p className="font-sans text-white/70 text-xs">{prenda.categoria} · {prenda.color}</p>
                    </div>
                  </div>
                );
              })}
              {previewLook.prendas_ids.length === 0 && (
                <div className="col-span-2 aspect-[16/9] bg-brand-gray-50 rounded-xl flex items-center justify-center text-brand-gray-300">
                  <p className="font-sans text-sm">No hay prendas seleccionadas</p>
                </div>
              )}
            </div>

            {previewLook.notas && (
              <div className="bg-brand-cream rounded-xl p-4">
                <p className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider font-medium mb-1">Notas</p>
                <p className="font-sans text-brand-black text-sm">{previewLook.notas}</p>
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              {!previewFromForm && previewLook.estado === 'planeado' && (
                <button
                  onClick={() => {
                    handleConfirmDay(previewLook.id);
                    closePreview();
                  }}
                  className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-check-line" />
                  Confirmar día
                </button>
              )}
              {!previewFromForm && (
                <button
                  onClick={() => {
                    setEditing(previewLook);
                    closePreview();
                  }}
                  className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-pencil-line" />
                  Editar
                </button>
              )}
              {previewFromForm && (
                <button
                  onClick={closePreview}
                  className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
                >
                  Cerrar vista previa
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}